import * as React from 'react'
import styled from 'styled-components'
import DeliveryIcon from 'assets/availability-white.svg'
import DeliveryIconBulky from 'assets/availability-bulky-white.svg'
import BoxIcon from 'assets/box.svg'
import calculateDeliveryDate from 'utils/calculateDeliveryDate'
import useTranslations from 'hooks/useTranslations'
import theme from 'theme'
import { useDisplayVariant } from 'modules/productDetail'
import DeliveryInfo from 'theme/molecules/DeliveryInfo'

type Props = {
  amount: number
  isBulky?: boolean
  showDeliveryTime?: boolean
  'data-cy-state'?: string
}

export default function DeliveryBadge(props: Props) {
  const t = useTranslations<'templates-PDP'>()
  const variant = useDisplayVariant()
  const deliveryInformations = React.useMemo(
    () => calculateDeliveryDate(variant.data, props.amount),
    [variant.data, props.amount]
  )

  if (
    variant.data.isDummy ||
    (deliveryInformations.icon === 'warning' && !props.showDeliveryTime)
  )
    return null

  return (
    <Wrapper data-cy-state={props['data-cy-state']}>
      {deliveryInformations.isBuyable &&
        deliveryInformations.icon === 'available' && (
          <div className="delivery badge" data-cy-state="show-delivery-badge">
            {props.isBulky ? (
              <DeliveryIconBulky data-cy-state="available-bulky" />
            ) : (
              <DeliveryIcon data-cy-state="available-regular" />
            )}
            <label className="delivery-text">{t('deliverable')}</label>
          </div>
        )}
      {deliveryInformations.isBuyable &&
        deliveryInformations.icon === 'warning' &&
        props.showDeliveryTime && (
          <div data-cy-state="show-delivery-soon-info-text">
            <DeliveryInfo
              variant={variant.data}
              amount={props.amount}
              hideIcon
            />
          </div>
        )}
      {!deliveryInformations.isBuyable && (
        <div
          className="non-delivery badge"
          data-cy-state="show-non-delivery-badge"
        >
          <BoxIcon />
          <label className="delivery-text">{t('non_deliverable')}</label>
        </div>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  .badge {
    width: fit-content;
    height: fit-content;
    padding: 6px 12px;
    display: flex;
    align-items: center;
    ${theme.rounding('m')};
    border-bottom-left-radius: 0;
    > svg {
      vertical-align: middle;
      min-width: 1.35rem;
      min-height: 1.35rem;
      margin-right: 0.625rem;
      > path {
        fill: ${theme.colors.white};
      }
      > g > g {
        filter: none;
        > g > path {
          fill: ${theme.colors.white};
        }
      }
    }
    &.delivery {
      background-color: ${theme.colors.accent.green};
    }
    &.delayed {
      background-color: ${theme.colors.warning};
    }
    &.non-delivery {
      background-color: ${theme.colors.shade.b4};
    }

    > .delivery-text {
      ${theme.ty('rc-xs', 'bold', 'rc-s')};
      color: ${theme.colors.white};
    }
  }
`
